import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'src/app/core/auth/auth.service';
import { AppRoutes } from 'src/app/shared/routes/routes';
import { SideBarService } from 'src/app/shared/side-bar/side-bar.service';
import { environment } from 'src/environments/environment';
import { AppUser } from 'src/app/core/models/users.model';
import { ImpersonationService } from 'src/app/core/auth/impersonation.service';
import { ExternalScriptsService } from 'src/app/core/services/external-scripts.service';
import { LocationForSelection } from 'src/app/core/auth/auth.interface';
import { LocationContextService } from 'src/app/core/services/location-context.service';
import {
  LocationSelectionDialogComponent,
  LocationSelectionData,
  LocationSelectionResult
} from 'src/app/pages/authentication/login/dialogs/location-selection-dialog/location-selection-dialog.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private static readonly ADMIN_VIEW_LOCATION_ID = -1;

  routes = AppRoutes;
  openBox = false;
  miniSidebar = false;
  user!: AppUser;
  usuario!: AppUser;
  user_id: number = 0;
  avatar: string = '';
  locationId: number = 0;
  role = '';
  rolesToCanEditProfile = ['SUPERADMIN', 'ADMIN', 'OWNER', 'MANAGER', 'DOCTOR'];
  rolesToCanViewProfile = ['SUPERADMIN', 'ADMIN', 'OWNER', 'MANAGER', 'DOCTOR', 'LM', 'BCBA', 'RBT'];
  rolesToNavigate = ['SUPERADMIN', 'ADMIN', 'OWNER', 'MANAGER', 'BCBA', 'RBT'];
  link: string[] = [];

  imagenSerUrl = environment.url_media;
  isImpersonating = false;
  currentLocationName: string = '';

  private destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly sideBar = inject(SideBarService);
  private readonly authService = inject(AuthService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly externalScriptsService = inject(ExternalScriptsService);
  private readonly dialog = inject(MatDialog);
  private readonly locationContextService = inject(LocationContextService);

  constructor() {
    this.sideBar.toggleSideBar.subscribe((res: string) => {
      this.miniSidebar = res === 'true';
    });
  }

  openBoxFunc() {
    this.openBox = !this.openBox;
    /* eslint no-var: off */
    var mainWrapper = document.getElementsByClassName('main-wrapper')[0];
    if (this.openBox) {
      mainWrapper.classList.add('open-msg-box');
    } else {
      mainWrapper.classList.remove('open-msg-box');
    }
  }

  ngOnInit(): void {
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: user => {
          this.user = user as AppUser;
          this.role = this.user?.roles?.[0];
          this.locationId = this.user?.location_id;
          this.setLink();
          this.updateCurrentLocationName();
        },
        error: err => console.log('HeaderComponent: Error in user$ subscription:', err),
        complete: () => console.log('HeaderComponent: user$ subscription completed')
      });

    this.activatedRoute.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        this.user_id = resp['id'];
      });

    // Subscribe to impersonation state to hide logout button when impersonating
    this.impersonationService.impersonationState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isImpersonating = state.isActive;
      });

    // Subscribe to location context changes
    this.locationContextService.currentLocationId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateCurrentLocationName();
      });

    this.locationContextService.isAdminViewMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateCurrentLocationName();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSideBar(): void {
    this.sideBar.switchSideMenuPosition();
  }
  toggleMobileSideBar(): void {
    this.sideBar.switchMobileSideBarPosition();
  }

  logout() {
    this.authService.logout().subscribe(() => {
      this.externalScriptsService.removeScript('widget-login');
      this.externalScriptsService.loadNotLoggedWidget();

      this.router.navigate([AppRoutes.auth.login]);
    });
  }

  public setLink(): void {
    this.link = [this.routes.dashboard.dashboard];
  }

  goProfile(userId: number): void {
    this.router.navigate([this.routes.doctors.profile, userId], {
      replaceUrl: true
    });
  }

  changeLocation(): void {
    this.authService.getUserLocations().subscribe({
      next: (response) => {
        this.openLocationDialogOrShowInfo(response.locations);
      },
      error: () => {
        const fallbackLocations = this.user?.locations || [];
        if (fallbackLocations.length > 0) {
          this.openLocationDialogOrShowInfo(fallbackLocations);
          return;
        }

        Swal.fire({
          title: 'Failed to Load Locations',
          text: 'Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });
  }

  private openLocationDialogOrShowInfo(locations: LocationForSelection[]): void {
    const isAdminViewMode = this.locationContextService.isAdminViewMode();

    const currentLocationId = this.locationContextService.getCurrentLocationId();
    const availableOptions = isAdminViewMode
      ? locations.filter((location) => !location.is_admin_view && location.id !== HeaderComponent.ADMIN_VIEW_LOCATION_ID)
      : locations.filter((location) => location.id !== currentLocationId);

    if (availableOptions.length > 0) {
      this.showLocationSelectionDialog(availableOptions, true);
      return;
    }

    Swal.fire({
      title: 'No Locations Available',
      text: 'You do not have access to any locations.',
      icon: 'info',
      confirmButtonText: 'OK'
    });
  }

  showLocationSelectionDialog(locations: LocationForSelection[], afterLogin: boolean = false): void {
    const dialogData: LocationSelectionData = {
      locations,
      title: 'Change Location',
      message: 'Select a location to switch to',
      afterLogin: afterLogin
    };

    const dialogRef = this.dialog.open<
      LocationSelectionDialogComponent,
      LocationSelectionData,
      LocationSelectionResult
    >(LocationSelectionDialogComponent, {
      width: '800px',
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.locationId) {
        this.handleLocationChange(result.locationId);
      }
    });
  }

  handleLocationChange(locationId: number): void {
    const isCurrentAdminView = this.locationContextService.isAdminViewMode();
    const isAdminSelection = locationId === HeaderComponent.ADMIN_VIEW_LOCATION_ID;
    const isSameSelection = (isCurrentAdminView && isAdminSelection) ||
      (!isCurrentAdminView && !isAdminSelection && locationId === this.locationContextService.getCurrentLocationId());

    // Don't change if it's the same context
    if (isSameSelection) {
      Swal.fire({
        title: 'Same Location',
        text: isCurrentAdminView
          ? 'You are already in Admin View.'
          : 'You are already in this location.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.authService.changeLocation(locationId).subscribe({
      next: () => {
        Swal.fire({
          title: 'Success!',
          text: 'Location changed successfully. The page will reload.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });

        // Force a full reload on the unified home URL so the next context never renders the previous view.
        setTimeout(() => {
          window.location.assign(AppRoutes.dashboard.dashboardUnique);
        }, 1500);
      },
      error: (error) => {
        Swal.fire({
          title: 'Failed to Change Location',
          text: error.message || 'Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });
  }

  updateCurrentLocationName(): void {
    if (this.locationContextService.isAdminViewMode()) {
      this.currentLocationName = 'Admin View';
      return;
    }

    const currentLocationId = this.locationContextService.getCurrentLocationId();
    if (currentLocationId && this.user?.locations) {
      const location = this.user.locations.find(loc => loc.id === currentLocationId);
      this.currentLocationName = location?.title || '';
    } else {
      this.currentLocationName = '';
    }
  }

}
